import { Tier } from '@mondaygpt/constants'
import { Package } from '@revenuecat/purchases-js'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from 'components'
import { X } from 'lucide-react'
import { TrialCheckout } from 'modules/onboarding/components/shared'
import { useEffect, useState } from 'react'
import type { PaywallSource } from '../slices'
import { pricingDialogProps } from '../pricing-theme'
import { BillingPeriod, PlanPicker } from './PlanPicker'

const PAYWALL_DIALOG_BASE = 'mx-auto w-full max-w-[calc(100vw-32px)]'

function PaywallLoadingBody() {
  return (
    <div className="text-center">
      <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-acid" />
      <p className="text-paper-dim">Loading...</p>
    </div>
  )
}

interface PaywallViewProps {
  isOpen: boolean
  isLoading: boolean
  isProcessing: boolean
  isConfigured: boolean
  errorMessage: string | null
  revenueCatError: string | null
  source?: PaywallSource
  onClose: () => void
  offering?: import('@revenuecat/purchases-js').Offering | null
  onPurchasePackage?: (pkg: Package, tier: Tier, period: BillingPeriod) => Promise<void>
  allowClose?: boolean
}

export const PaywallView = ({
  isOpen,
  isLoading,
  isProcessing,
  isConfigured,
  errorMessage,
  revenueCatError,
  source,
  onClose,
  offering,
  onPurchasePackage,
  allowClose = true,
}: PaywallViewProps) => {
  const [currentStep, setCurrentStep] = useState<'plan-picker' | 'checkout'>('plan-picker')
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null)
  const [purchaseError, setPurchaseError] = useState<string | null>(null)
  const [purchasing, setPurchasing] = useState(false)

  const preventDismiss = !allowClose
  const dismissGuardProps = preventDismiss
    ? {
        onEscapeKeyDown: (e: Event) => e.preventDefault(),
        onInteractOutside: (e: Event) => e.preventDefault(),
      }
    : {}

  useEffect(() => {
    if (isOpen) {
      setCurrentStep('plan-picker')
      setSelectedPackage(null)
      setPurchaseError(null)
    }
  }, [isOpen])

  if (isLoading && !isConfigured) {
    return (
      <Dialog open={isOpen} onOpenChange={allowClose ? onClose : undefined}>
        <DialogPortal>
          <DialogOverlay />
          <DialogContent
            {...pricingDialogProps(`${PAYWALL_DIALOG_BASE} p-8 sm:max-w-md`)}
            withCloseButton={allowClose}
            {...dismissGuardProps}
          >
            <DialogTitle className="sr-only">Loading subscription options</DialogTitle>
            <DialogDescription className="sr-only">
              Please wait while we load your subscription options.
            </DialogDescription>
            <PaywallLoadingBody />
          </DialogContent>
        </DialogPortal>
      </Dialog>
    )
  }

  const handleSelectPlan = async (pkg: Package | null, tier: Tier, period: BillingPeriod) => {
    if (period === 'yearly' && pkg) {
      setSelectedPackage(pkg)
      setCurrentStep('checkout')
      return
    }

    if (!pkg || !onPurchasePackage) {
      setPurchaseError('This plan is not available yet. Please try yearly.')
      return
    }

    setPurchasing(true)
    setPurchaseError(null)
    try {
      await onPurchasePackage(pkg, tier, period)
      onClose()
    } catch (error) {
      setPurchaseError(error instanceof Error ? error.message : 'Purchase failed')
    } finally {
      setPurchasing(false)
    }
  }

  if (currentStep === 'checkout' && selectedPackage) {
    return (
      <Dialog open={isOpen && !isProcessing} onOpenChange={allowClose ? onClose : undefined}>
        <DialogPortal>
          <DialogOverlay />
          <DialogContent
            {...pricingDialogProps(`${PAYWALL_DIALOG_BASE} p-0 sm:max-w-md`)}
            withCloseButton={allowClose}
            {...dismissGuardProps}
          >
            <DialogTitle className="sr-only">Complete your checkout</DialogTitle>
            <DialogDescription className="sr-only">
              Confirm your plan and checkout.
            </DialogDescription>
            <TrialCheckout
              onNext={onClose}
              onBack={() => setCurrentStep('plan-picker')}
              isSubmitting={false}
              onClose={onClose}
              source={source || 'feature_gate'}
              selectedPackage={selectedPackage}
            />
          </DialogContent>
        </DialogPortal>
      </Dialog>
    )
  }

  return (
    <Dialog open={isOpen && !isProcessing} onOpenChange={allowClose ? onClose : undefined}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent
          {...pricingDialogProps(
            `${PAYWALL_DIALOG_BASE} overflow-y-auto rounded-xl bg-bg p-0 shadow-xl sm:max-w-2xl sm:rounded-2xl`
          )}
          withCloseButton={false}
          {...dismissGuardProps}
        >
          <DialogTitle className="sr-only">Choose a plan</DialogTitle>
          <DialogDescription className="sr-only">
            Select a plan to start using Taka.
          </DialogDescription>

          {allowClose && (
            <div className="absolute top-4 right-4 z-10">
              <button
                onClick={onClose}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-bg-3 transition-colors hover:bg-bg-4"
              >
                <X className="h-4 w-4 text-paper-dim" />
              </button>
            </div>
          )}

          <div className="px-6 py-8 pt-12">
            <PlanPicker
              offering={offering ?? null}
              onSelectPlan={handleSelectPlan}
              isProcessing={purchasing || isProcessing}
              errorMessage={purchaseError || errorMessage || revenueCatError}
            />
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}
